import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Close as CloseIcon,
  Assignment as AssignmentIcon,
  School as SchoolIcon,
  Description as DescriptionIcon,
  Security as SecurityIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { API_URL } from '../../config/api';
import { useAuth } from '../../context/AuthContext';

const CompletionStep = ({ data, onChange }) => {
  const { user, getAuthHeaders } = useAuth();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [checkedItems, setCheckedItems] = useState({});
  const [checklistItems, setChecklistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentStepData, setCurrentStepData] = useState(null);

  // Get onboarding data from parent component
  const onboardingData = data;

  useEffect(() => {
    const fetchChecklistData = async () => {
      try {
        setLoading(true);
        
        // Fetch current step data to get completion status
        const currentStepResponse = await fetch(`${API_URL}/onboarding/current-step/${user.id}`, {
          headers: getAuthHeaders(),
        });
        const currentStepData = currentStepResponse.ok ? await currentStepResponse.json() : null;
        setCurrentStepData(currentStepData);
        
        // Fetch induction data
        const inductionResponse = await fetch(`${API_URL}/onboarding/Induction?userId=${user.id}`, {
          headers: getAuthHeaders(),
        });
        const inductionData = inductionResponse.ok ? await inductionResponse.json() : null;

        // Fetch onboarding form data
        const formsResponse = await fetch(`${API_URL}/onboarding/onboarding-form/${user.id}`, {
          headers: getAuthHeaders(),
        });
        const formsData = formsResponse.ok ? await formsResponse.json() : null;

        // Fetch process docs validation
        const docsResponse = await fetch(`${API_URL}/onboarding/process-docs-validation/${user.id}`, {
          headers: getAuthHeaders(),
        });
        const docsData = docsResponse.ok ? await docsResponse.json() : null;

        // Create dynamic checklist based on API data
        const dynamicChecklist = [
          {
            id: 'induction_complete',
            label: 'Induction Complete',
            description: 'Company overview video and policies reviewed',
            required: true,
            completed: inductionData?.completed === true,
            apiData: inductionData,
          },
          {
            id: 'forms_submitted',
            label: 'Onboarding Forms Submitted',
            description: 'Personal information and documents submitted',
            required: true,
            completed: formsData?.success === true && formsData?.data !== null,
            apiData: formsData,
          },
          {
            id: 'documents_processed',
            label: 'Documents Processed',
            description: 'Required documents uploaded and verified',
            required: true,
            completed: docsData?.success === true && docsData?.isValid === true,
            apiData: docsData,
          },
          {
            id: 'training_completed',
            label: 'Training Modules Completed',
            description: 'All mandatory training modules finished',
            required: true,
            completed: onboardingData?.training?.completed && onboardingData.training.completed.length > 0,
            apiData: onboardingData?.training,
          },
          {
            id: 'access_provisioned',
            label: 'System Access Granted',
            description: 'IT has provided necessary system access',
            required: true,
            completed: onboardingData?.accessProvisioning?.systems && onboardingData.accessProvisioning.systems.length > 0,
            apiData: onboardingData?.accessProvisioning,
          },
        ];

        setChecklistItems(dynamicChecklist);
        
        // Initialize checked items based on completion status
        const initialCheckedItems = dynamicChecklist.reduce((acc, item) => ({
          ...acc,
          [item.id]: item.completed
        }), {});
        setCheckedItems(initialCheckedItems);

      } catch (error) {
        console.error('Error fetching checklist data:', error);
        toast.error('Failed to load onboarding progress');
      } finally {
        setLoading(false);
      }
    };

    if (user?.id) {
      fetchChecklistData();
    }
  }, [user, getAuthHeaders, onboardingData]);

  const handleCheckboxChange = (itemId) => {
    setCheckedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Simulate API call for onboarding completion
      await new Promise((resolve) => setTimeout(resolve, 2000));

      const allRequiredChecked = checklistItems
        .filter((item) => item.required)
        .every((item) => checkedItems[item.id]);

      if (!allRequiredChecked) {
        toast.error('Please complete all required items before submitting');
        return;
      }

      // Update only the completion data
      onChange({
        checklist: checkedItems,
        status: 'completed',
        completedAt: new Date().toISOString(),
      });
      toast.success('Onboarding process completed successfully!');
      setConfirmDialogOpen(false);
    } catch (error) {
      toast.error('Failed to complete onboarding process');
    } finally {
      setSubmitting(false);
    }
  };

  const getStepIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'in-progress':
        return <WarningIcon color="warning" />;
      case 'pending':
        return <ErrorIcon color="error" />;
      default:
        return null;
    }
  };

  // Get real onboarding steps data
  const getRealOnboardingSteps = () => {
    if (!onboardingData || !currentStepData) return [];

    // Use backend data to determine step completion
    const getStepStatus = (stepIndex) => {
      if (currentStepData.currentStep > stepIndex) return 'completed';
      if (currentStepData.currentStep === stepIndex) return 'in-progress';
      return 'pending';
    };

    return [
      {
        id: 'induction',
        label: 'Induction',
        description: 'Company overview and introduction',
        icon: <PersonIcon />,
        status: getStepStatus(0),
        details: getStepStatus(0) === 'completed' 
          ? 'Completed company overview video and documentation review' 
          : getStepStatus(0) === 'in-progress'
          ? 'Currently in progress'
          : 'Pending company overview and policies review',
        data: onboardingData.induction || {},
      },
      {
        id: 'forms',
        label: 'Forms & Documents',
        description: 'Personal information and document submission',
        icon: <DescriptionIcon />,
        status: getStepStatus(1),
        details: getStepStatus(1) === 'completed' 
          ? 'Submitted all required forms and documents' 
          : getStepStatus(1) === 'in-progress'
          ? 'Currently in progress'
          : 'Pending form submission',
        data: onboardingData.forms || {},
      },
      {
        id: 'documents',
        label: 'Document Processing',
        description: 'Document verification and approval',
        icon: <AssignmentIcon />,
        status: getStepStatus(2),
        details: getStepStatus(2) === 'completed' 
          ? 'Documents submitted for processing' 
          : getStepStatus(2) === 'in-progress'
          ? 'Currently in progress'
          : 'Pending document submission',
        data: onboardingData.processDocs || {},
      },
      {
        id: 'training',
        label: 'Training',
        description: 'Mandatory training modules',
        icon: <SchoolIcon />,
        status: getStepStatus(3),
        details: getStepStatus(3) === 'completed' 
          ? `${onboardingData.training?.completed?.length || 0} training modules completed` 
          : getStepStatus(3) === 'in-progress'
          ? 'Currently in progress'
          : 'No training modules completed yet',
        data: onboardingData.training || {},
      },
      {
        id: 'completion',
        label: 'Completion',
        description: 'Final steps and system access',
        icon: <SecurityIcon />,
        status: getStepStatus(4),
        details: getStepStatus(4) === 'completed' 
          ? 'Onboarding process completed successfully' 
          : getStepStatus(4) === 'in-progress'
          ? 'Currently completing final steps'
          : 'Pending final completion steps',
        data: onboardingData.completion || {},
      },
    ];
  };

  const getCompletionStatus = () => {
    if (!currentStepData) return 0;
    
    // If currentStep is 5 (all steps completed), return 100%
    if (currentStepData.currentStep >= 5) return 100;
    
    // Calculate percentage based on current step
    // currentStep represents the first incomplete step, so completed steps = currentStep
    const completedSteps = currentStepData.currentStep;
    const totalSteps = 5; // Total onboarding steps
    
    return (completedSteps / totalSteps) * 100;
  };

  const realOnboardingSteps = getRealOnboardingSteps();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontSize: '20px', fontWeight: 600 }}>
          Onboarding Completion
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '16px' }}>
          Review and complete the final steps of your onboarding process
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ fontSize: '18px', fontWeight: 600 }}>
                  Onboarding Progress
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {Math.round(getCompletionStatus())}% Complete
                  </Typography>
                  <Box sx={{ 
                    width: 60, 
                    height: 8, 
                    backgroundColor: '#e0e0e0', 
                    borderRadius: 4,
                    overflow: 'hidden'
                  }}>
                    <Box sx={{ 
                      width: `${getCompletionStatus()}%`, 
                      height: '100%', 
                      backgroundColor: '#4caf50',
                      transition: 'width 0.3s ease'
                    }} />
                  </Box>
                </Box>
              </Box>
              
              <Stepper orientation="vertical">
                {realOnboardingSteps.map((step) => (
                  <Step key={step.id} active={step.status !== 'pending'}>
                    <StepLabel
                      icon={step.icon}
                      optional={
                        <Typography variant="caption" color="text.secondary">
                          {step.details}
                        </Typography>
                      }
                    >
                      {step.label}
                    </StepLabel>
                    <StepContent>
                      <Typography variant="body2" color="text.secondary">
                        {step.description}
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        {getStepIcon(step.status)}
                      </Box>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontSize: '18px', fontWeight: 600 }}>
                Completion Checklist
              </Typography>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : checklistItems.length > 0 ? (
                <List>
                  {checklistItems.map((item) => (
                    <ListItem key={item.id} sx={{ 
                      flexDirection: 'column', 
                      alignItems: 'flex-start',
                      p: 2,
                      mb: 1,
                      borderRadius: 1,
                      backgroundColor: item.completed ? '#f1f8e9' : '#fff3e0',
                      border: `1px solid ${item.completed ? '#c8e6c9' : '#ffe0b2'}`
                    }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        width: '100%',
                        mb: 1
                      }}>
                        <Checkbox
                          edge="start"
                          checked={checkedItems[item.id] || false}
                          onChange={() => handleCheckboxChange(item.id)}
                          disabled={item.required && !item.completed}
                          color={item.completed ? 'success' : 'default'}
                          sx={{ mr: 1 }}
                        />
                        <Typography variant="subtitle2" sx={{ 
                          fontWeight: 600,
                          flex: 1,
                          fontSize: '16px'
                        }}>
                          {item.label}
                        </Typography>
                        {item.required && (
                          <Box sx={{ 
                            px: 1.5, 
                            py: 0.5, 
                            borderRadius: 1,
                            backgroundColor: item.completed ? '#4caf50' : '#f44336',
                            color: 'white',
                            minWidth: 'fit-content'
                          }}>
                            <Typography variant="caption" sx={{ 
                              fontWeight: 600,
                              fontSize: '0.7rem'
                            }}>
                              {item.completed ? '✓ Completed' : 'Required'}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ 
                        ml: 6,
                        fontSize: '16px',
                        lineHeight: 1.4
                      }}>
                        {item.description}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box sx={{ py: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No checklist items available
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          size="medium"
          onClick={() => setConfirmDialogOpen(true)}
          disabled={
            submitting ||
            !checklistItems
              .filter((item) => item.required)
              .every((item) => checkedItems[item.id])
          }
          sx={{ fontSize: '14px', fontWeight: 600 }}
        >
          Complete Onboarding
        </Button>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '18px', fontWeight: 600 }}>
          Confirm Onboarding Completion
          <IconButton
            aria-label="close"
            onClick={() => setConfirmDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Please confirm that you have completed all required steps and have
            access to all necessary resources.
          </Alert>
          <Typography variant="body2" paragraph sx={{ fontSize: '16px' }}>
            By completing the onboarding process, you confirm that all steps have been completed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} sx={{ fontSize: '14px', fontWeight: 600 }}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
            sx={{ fontSize: '14px', fontWeight: 600 }}
          >
            {submitting ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Completing...
              </>
            ) : (
              'Confirm Completion'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompletionStep; 